import { useCallback, useEffect, useState } from "react";
import { Button, Card, Checkbox, Divider, Empty, Input, Modal, Segmented, Space, Table, Tag, Tooltip, Typography, Upload, message } from "antd";
import { Link } from "react-router-dom";
import { listMyTasks, listMyDecisions, approveTask, rejectTask, updateTaskProgress } from "../../apiservice/workflow";
import { getInstitutionApi } from "../../apiservice/setting";
import { recordViewPath } from "../Workflows/recordLink";
import PurchaseOrderVoucherBody from "../Order/Purchase Order/PurchaseOrderVoucher/components/PurchaseOrderVoucherBody";
import SalesOrderVoucherBody from "../Order/Sales Order/SalesOrderVoucher/components/SalesOrderVoucherBody";
import LeaveRequestDetail from "./LeaveRequestDetail";

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

function RecordLink({ module, recordType, recordId }) {
  const typed = module || recordType || "";
  const path = recordViewPath(typed, recordId)
    || `/workflows?record_type=${encodeURIComponent(typed)}&record_id=${encodeURIComponent(recordId)}`;
  const label = <Text code style={{ color: "inherit" }}>{String(recordId).slice(0, 8)}…</Text>;
  return (
    <Tooltip title={`Open ${typed || "workflow"} record`}>
      <Link to={path} style={{ color: "#1677ff" }}>{label}</Link>
    </Tooltip>
  );
}

const DECISION_COLORS = { Approved: "green", Rejected: "red", Cancelled: "default" };

/**
 * "My approvals" inbox. Two views:
 *   - Pending          : tasks awaiting the signed-in user's decision (approve / reject)
 *   - Approved/Rejected : tasks the user has already decided on (history)
 * Approve and reject both accept an optional remark.
 */
export default function WorkflowTasks({ embedded = false }) {
  const [view, setView] = useState("Pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Decision modal: { task, type: 'approve' | 'reject' }
  const [decision, setDecision] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [taskForm, setTaskForm] = useState(null);
  const [taskChecklist, setTaskChecklist] = useState([]);
  const [taskFileList, setTaskFileList] = useState([]);
  const [institution, setInstitution] = useState(null);

  // Fetched once, lazily, only if a task ever actually needs record detail
  // rendered (currently purchase_order and sales_order) — avoids the call
  // for users who never open one.
  useEffect(() => {
    if (["purchase_order", "sales_order"].includes(decision?.task?.record_type) && !institution) {
      getInstitutionApi().then((res) => setInstitution(res.data)).catch(() => {});
    }
  }, [decision, institution]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = view === "Pending"
        ? await listMyTasks("Pending")
        : await listMyDecisions("Approved,Rejected");
      setRows(r?.data?.data || r?.data || []);
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to load your tasks");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { refresh(); }, [refresh]);

  function openDecision(task, type) {
    setRemarks("");
    setDecision({ task, type });
  }

  async function submitDecision() {
    if (!decision) return;
    const { task, type } = decision;
    setSubmitting(true);
    setBusyId(task.id);
    try {
      if (type === "approve") await approveTask(task.id, remarks || null);
      else await rejectTask(task.id, remarks || null);
      message.success(type === "approve" ? "Approved" : "Rejected");
      setDecision(null);
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || `${type} failed`);
    } finally {
      setSubmitting(false);
      setBusyId(null);
    }
  }

  function openTaskForm(task) {
    const progress = task.task_progress || {};
    const preChecklist = Array.isArray(progress.checklist) ? progress.checklist : [];
    const preFiles = Array.isArray(progress.files) ? progress.files : [];
    setTaskChecklist(preChecklist);
    setTaskFileList(
      preFiles.map((name, idx) => ({
        uid: `existing-${idx}-${name}`,
        name,
        status: "done",
      }))
    );
    setTaskForm(task);
  }

  async function submitTaskForm() {
    if (!taskForm) return;
    setSubmitting(true);
    setBusyId(taskForm.id);
    try {
      const files = (taskFileList || []).map((f) => f.name).filter(Boolean);
      const res = await updateTaskProgress(taskForm.id, { checklist: taskChecklist, files });
      if (res?.error) throw new Error(res?.message || "Task update failed");
      if (res?.data?.auto_completed || res?.auto_completed) {
        message.success("Task completed");
        setTaskForm(null);
      } else {
        message.info("Progress saved. Complete required checklist/file items to finish.");
      }
      refresh();
    } catch (e) {
      message.error(e?.message || "Failed to update task progress");
    } finally {
      setSubmitting(false);
      setBusyId(null);
    }
  }

  const pendingColumns = [
    { title: "Task", dataIndex: "id", width: 70 },
    { title: "Workflow", dataIndex: "workflow_name", render: (v, r) => v || `#${r.workflow_id}`, ellipsis: true },
    { title: "Module", dataIndex: "module", width: 120, render: (v, r) => <Text code>{v || r.record_type}</Text> },
    { title: "Record", dataIndex: "record_id", width: 110, render: (v, r) => <RecordLink module={r.module} recordType={r.record_type} recordId={v} /> },
    { title: "Step", dataIndex: "node_id", width: 170, render: (_, r) => <Text>{r.node_label || r.node_id}</Text> },
    {
      title: "Due",
      dataIndex: "due_at",
      width: 180,
      render: (v) => (v
        ? <Tag color={new Date(v) < new Date() ? "red" : "default"}>{new Date(v).toLocaleString()}</Tag>
        : <Text type="secondary">—</Text>),
    },
    {
      title: "Actions",
      width: 180,
      fixed: "right",
      render: (_, r) => (
        r.action_type === "Assign Task" ? (
          <Button size="small" type="primary" loading={busyId === r.id} onClick={() => openTaskForm(r)}>
            Complete Task
          </Button>
        ) : (
          <Space>
            <Button size="small" type="primary" loading={busyId === r.id} onClick={() => openDecision(r, "approve")}>
              Approve
            </Button>
            <Button size="small" danger loading={busyId === r.id} onClick={() => openDecision(r, "reject")}>
              Reject
            </Button>
          </Space>
        )
      ),
    },
  ];

  const decidedColumns = [
    { title: "Task", dataIndex: "id", width: 70 },
    { title: "Workflow", dataIndex: "workflow_name", render: (v, r) => v || `#${r.workflow_id}`, ellipsis: true },
    { title: "Module", dataIndex: "module", width: 120, render: (v, r) => <Text code>{v || r.record_type}</Text> },
    { title: "Record", dataIndex: "record_id", width: 110, render: (v, r) => <RecordLink module={r.module} recordType={r.record_type} recordId={v} /> },
    { title: "Decision", dataIndex: "status", width: 110, render: (s) => <Tag color={DECISION_COLORS[s] || "default"}>{s}</Tag> },
    { title: "Remark", dataIndex: "remarks", ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: "Decided at",
      dataIndex: "approved_at",
      width: 180,
      render: (v) => (v ? new Date(v).toLocaleString() : <Text type="secondary">—</Text>),
    },
  ];

  return (
    <div style={{ padding: embedded ? 0 : 16, maxWidth: 1100 }}>
      {!embedded && (
        <>
          <Title level={3} style={{ marginTop: 0 }}>My tasks</Title>
          <Paragraph type="secondary">
            Workflow tasks waiting on your action. Approval steps show Approve/Reject, while Assign Task steps open a completion form.
          </Paragraph>
          <Paragraph type="secondary" style={{ marginTop: -8 }}>
            Info: This includes steps from <b>Assign to User</b>, <b>Assign to Role</b>, and <b>Assign Task</b> actions.
          </Paragraph>
        </>
      )}
      <Card
        loading={loading}
        title={<Segmented value={view} onChange={setView} options={["Pending", "Approved/Rejected"]} />}
      >
        <Table
          rowKey="id"
          dataSource={rows}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: <Empty description={view === "Pending" ? "No pending approvals 🎉" : "No decisions yet"} /> }}
          pagination={{ pageSize: 10 }}
          columns={view === "Pending" ? pendingColumns : decidedColumns}
        />
      </Card>

      <Modal
        open={!!decision}
        title={decision?.type === "approve" ? "Approve task" : "Reject task"}
        okText={decision?.type === "approve" ? "Approve" : "Reject"}
        okButtonProps={{ danger: decision?.type === "reject", loading: submitting }}
        onOk={submitDecision}
        onCancel={() => setDecision(null)}
        destroyOnClose
        width={["purchase_order", "sales_order", "leave_request"].includes(decision?.task?.record_type) ? 900 : undefined}
      >
        {decision?.task?.record_type === "leave_request" && (
          <>
            <div style={{ maxHeight: 500, overflowY: "auto", border: "1px solid #eee", borderRadius: 4, padding: 12, marginBottom: 12 }}>
              <LeaveRequestDetail leaveRequestId={decision.task.record_id} />
            </div>
            <Divider style={{ margin: "8px 0" }} />
          </>
        )}
        {decision?.task?.record_type === "purchase_order" && (
          <>
            <div style={{ maxHeight: 500, overflowY: "auto", border: "1px solid #eee", borderRadius: 4, padding: 12, marginBottom: 12 }}>
              <PurchaseOrderVoucherBody pur_o_id={decision.task.record_id} institution={institution} />
            </div>
            <Paragraph type="secondary" style={{ marginTop: -4, marginBottom: 8, fontSize: 12 }}>
              Note: attachments aren't shown here — Purchase Orders don't currently support file attachments in this app.
            </Paragraph>
            <Divider style={{ margin: "8px 0" }} />
          </>
        )}
        {decision?.task?.record_type === "sales_order" && (
          <>
            <div style={{ maxHeight: 500, overflowY: "auto", border: "1px solid #eee", borderRadius: 4, padding: 12, marginBottom: 12 }}>
              <SalesOrderVoucherBody sale_o_id={decision.task.record_id} institution={institution} />
            </div>
            <Paragraph type="secondary" style={{ marginTop: -4, marginBottom: 8, fontSize: 12 }}>
              Note: attachments aren't shown here — Sales Orders don't currently support file attachments in this app.
            </Paragraph>
            <Divider style={{ margin: "8px 0" }} />
          </>
        )}
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {decision?.type === "approve"
            ? "Approving resumes the workflow."
            : "Rejecting cancels the workflow for this record."}
        </Paragraph>
        <Input.TextArea
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add a remark (optional)"
        />
      </Modal>

      <Modal
        open={!!taskForm}
        title={taskForm?.task_definition?.taskTitle || taskForm?.node_label || "Complete task"}
        okText="Save / Complete"
        okButtonProps={{ loading: submitting }}
        onOk={submitTaskForm}
        onCancel={() => setTaskForm(null)}
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {taskForm?.task_definition?.taskDescription || "Complete the checklist and add file names if required."}
        </Paragraph>

        {(taskForm?.task_definition?.checklist || []).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Checklist</Text>
            <div style={{ marginTop: 8 }}>
              <Checkbox.Group value={taskChecklist} onChange={(vals) => setTaskChecklist(vals)}>
                <Space direction="vertical">
                  {(taskForm?.task_definition?.checklist || []).map((item, idx) => (
                    <Checkbox key={item.id || idx} value={item.id}>
                      {item.label || `Item ${idx + 1}`}{item.required ? " *" : ""}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>
          </div>
        )}

        {taskForm?.task_definition?.allowFileUpload && (
          <div>
            <Text strong>Files</Text>
            <Dragger
              multiple
              beforeUpload={() => false}
              fileList={taskFileList}
              onChange={({ fileList }) => setTaskFileList(fileList)}
              openFileDialogOnClick
              style={{ marginTop: 8 }}
            >
              <p style={{ marginBottom: 6 }}><b>Click to select files</b> or drag and drop here</p>
              <p style={{ margin: 0, color: "#666" }}>Multiple files are supported.</p>
            </Dragger>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {taskForm?.task_definition?.fileUploadRequired ? "At least one file is required for completion." : "File input is optional."}
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
}
